import { useCallback, useEffect, useMemo, useState } from 'react';
import type { SetURLSearchParams } from 'react-router';
import { JOB_TYPES, TYPE_FILTERS, type CoverageDto } from '@mnemo/shared';
import type { Locale, TranslationKey } from '../i18n.js';
import { api } from '../lib/api.js';
import { describeRange, Icon, PeriodPicker, useDateRange, useDayFormat } from './DateRange.js';

type T = (key: TranslationKey, vars?: Record<string, string | number>) => string;

/**
 * Les filtres des travaux, partagés par les Journaux et le Décompte (DEC-02).
 *
 * Ils vivent dans l'URL (Charte §6) et les deux vues les lisent au même endroit : passer de
 * l'une à l'autre garde la sélection, et un lien rouvre exactement le même écran. Une
 * valeur inconnue dans l'URL retombe sur le défaut, en silence.
 */
export function useJobFilters(
  params: URLSearchParams,
  setParams: SetURLSearchParams,
  t: T,
  locale: Locale,
) {
  const dates = useDateRange(params, setParams);
  const day = useDayFormat(locale);
  const q = (params.get('q') ?? '').slice(0, 120);
  const type = TYPE_FILTERS.includes((params.get('type') ?? '') as never)
    ? params.get('type')
    : null;
  const establishment = params.get('etab');
  const costCenter = params.get('cc');
  const origin = params.get('orig');
  const user = params.get('user');
  const device = params.get('dev');
  const colorOnly = params.get('coul') === '1';

  /** Les filtres, traduits en paramètres d'API — sans tri ni pagination. */
  const query = useMemo(() => {
    const p = new URLSearchParams();
    if (dates.from) p.set('from', dates.from);
    if (dates.to) p.set('to', dates.to);
    if (q) p.set('q', q);
    if (type) p.set('type', type);
    if (establishment) p.set('establishment', establishment);
    if (origin) p.set('userEstablishment', origin);
    if (costCenter) p.set('costCenter', costCenter);
    if (user) p.set('user', user);
    if (device) p.set('device', device);
    if (colorOnly) p.set('color', '1');
    return p;
  }, [dates.from, dates.to, q, type, establishment, origin, costCenter, user, device, colorOnly]);

  const update = useCallback(
    (key: string, value: string | null) => {
      const next = new URLSearchParams(params);
      if (value) next.set(key, value);
      else next.delete(key);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  /** Les filtres posés, dits en toutes lettres et retirables un par un (VUE-12). */
  const rangeLabel = describeRange(t, day, dates.from, dates.to);
  const active = useMemo(
    () =>
      [
        rangeLabel && { key: 'dates', label: t('filter.period'), value: rangeLabel },
        establishment && { key: 'etab', label: t('table.establishment'), value: establishment },
        costCenter && { key: 'cc', label: t('table.costCenter'), value: costCenter },
        origin && { key: 'orig', label: t('filter.origin'), value: origin },
        user && { key: 'user', label: t('table.user'), value: user },
        device && { key: 'dev', label: t('table.device'), value: device },
        type && {
          key: 'type',
          label: t('filter.type'),
          value: t(`type.${type}` as TranslationKey),
        },
        colorOnly && { key: 'coul', label: t('filter.color'), value: t('filter.colorOnly') },
        q && { key: 'q', label: t('filter.searchShort'), value: q },
      ].filter((f): f is { key: string; label: string; value: string } => Boolean(f)),
    [rangeLabel, establishment, costCenter, origin, user, device, type, colorOnly, q, t],
  );

  /** Retirer un filtre ; la période en retire deux d'un coup, `du` et `au`. */
  const remove = useCallback(
    (key: string) => {
      if (key === 'dates') dates.setRange(null, null);
      else update(key, null);
    },
    [dates, update],
  );

  /** Tout retirer — sauf le tri, qui n'est pas un filtre. */
  const clear = useCallback(() => {
    const next = new URLSearchParams();
    for (const key of ['tri', 'sens']) {
      const value = params.get(key);
      if (value) next.set(key, value);
    }
    setParams(next, { replace: true });
  }, [params, setParams]);

  return {
    dates,
    day,
    q,
    type,
    establishment,
    costCenter,
    origin,
    user,
    device,
    colorOnly,
    query,
    update,
    active,
    remove,
    clear,
  };
}

export type JobFilters = ReturnType<typeof useJobFilters>;

/**
 * Une liste de la barre : compacte, et d'accent quand elle filtre — par le BORD et la
 * graisse, jamais par un fond translucide : sur Windows et Android, la liste ouverte
 * reprend le fond du <select>, composé sur du blanc (voir index.css).
 */
const selectClass = (active: boolean) =>
  `field min-w-[9.5rem] flex-1 py-0 pr-8 text-sm sm:min-w-0 sm:flex-none ${active ? 'border-2 border-accent font-semibold' : ''}`;

/**
 * La barre des filtres — UNE ligne sur un écran d'ordinateur (VUE-17). Les étiquettes vivent
 * dans les listes elles-mêmes (« Tous les types ») et dans `aria-label` ; un filtre posé se
 * reconnaît à son bord d'accent, et se lit en toutes lettres juste dessous.
 */
export function FilterBar({
  t,
  locale,
  filters,
  userId,
  exportHref,
  exportTitle,
}: {
  t: T;
  locale: Locale;
  filters: JobFilters;
  /** Recharger les listes quand l'identité change — voir le piège dans CLAUDE.md. */
  userId?: string;
  exportHref: string;
  exportTitle: string;
}) {
  const { dates, q, type, establishment, costCenter, colorOnly, update } = filters;
  const [establishments, setEstablishments] = useState<{ code: string; jobs: number }[]>([]);
  const [costCenters, setCostCenters] = useState<{ name: string; jobs: number }[]>([]);
  const numberFormat = useMemo(
    () => new Intl.NumberFormat(locale === 'de' ? 'de-CH' : 'fr-CH'),
    [locale],
  );

  // Le champ de recherche est contrôlé : retirer le filtre « Recherche » d'un clic doit
  // aussi vider la case, sans quoi elle afficherait un texte qui ne filtre plus rien.
  const [search, setSearch] = useState(q);
  useEffect(() => {
    setSearch((current) => (current.trim() === q ? current : q));
  }, [q]);

  // Secondaires : un échec ne doit ni bloquer ni alerter (Charte §5).
  useEffect(() => {
    api
      .establishments()
      .then((r) => setEstablishments(r.items))
      .catch(() => undefined);
  }, [userId]);

  // Les centres de coût suivent l'établissement choisi : on ne propose pas ceux d'une école
  // qu'on vient d'écarter.
  useEffect(() => {
    api
      .costCenters(establishment)
      .then((r) => setCostCenters(r.items))
      .catch(() => setCostCenters([]));
  }, [userId, establishment]);

  return (
    <section className="card flex flex-wrap items-center gap-2 p-2.5">
      <PeriodPicker
        t={t}
        locale={locale}
        from={dates.from}
        to={dates.to}
        onChange={dates.setRange}
      />

      <label className="relative flex min-w-[10rem] flex-1 items-center" title={t('filter.search')}>
        <span className="sr-only">{t('filter.search')}</span>
        <Icon name="search" className="pointer-events-none absolute left-3 text-muted" />
        <input
          type="search"
          className={`field w-full pl-9 text-sm ${q ? 'border-accent' : ''}`}
          value={search}
          placeholder={t('filter.searchPlaceholder')}
          onChange={(e) => {
            setSearch(e.target.value);
            update('q', e.target.value.trim() || null);
          }}
        />
      </label>

      <select
        aria-label={t('filter.type')}
        className={selectClass(Boolean(type))}
        value={type ?? ''}
        onChange={(e) => update('type', e.target.value || null)}
      >
        <option value="">{t('filter.type.all')}</option>
        {/* Le total facturable, en un seul choix — en principe, seules les impressions et
            les copies coûtent. Les types un par un restent dessous. */}
        <option value="print-copy">{t('type.print-copy')}</option>
        {JOB_TYPES.map((value) => (
          <option key={value} value={value}>
            {t(`type.${value}` as TranslationKey)}
          </option>
        ))}
      </select>

      {establishments.length > 1 && (
        <select
          aria-label={t('filter.establishment')}
          className={selectClass(Boolean(establishment))}
          value={establishment ?? ''}
          onChange={(e) => update('etab', e.target.value || null)}
        >
          <option value="">{t('filter.establishment.all')}</option>
          {establishments.map((e) => (
            <option key={e.code} value={e.code}>
              {e.code} ({numberFormat.format(e.jobs)})
            </option>
          ))}
        </select>
      )}

      {(costCenters.length > 0 || costCenter) && (
        <select
          aria-label={t('filter.costCenter')}
          className={`${selectClass(Boolean(costCenter))} max-w-[13rem]`}
          value={costCenter ?? ''}
          onChange={(e) => update('cc', e.target.value || null)}
        >
          <option value="">{t('filter.costCenter.all')}</option>
          {/* Un centre venu d'un lien reste affiché, même hors de la liste courante. */}
          {costCenter && !costCenters.some((c) => c.name === costCenter) && (
            <option value={costCenter}>{costCenter}</option>
          )}
          {costCenters.map((c) => (
            <option key={c.name} value={c.name}>
              {c.name} ({numberFormat.format(c.jobs)})
            </option>
          ))}
        </select>
      )}

      <button
        type="button"
        aria-pressed={colorOnly}
        onClick={() => update('coul', colorOnly ? null : '1')}
        title={t('filter.colorOnly')}
        className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg border px-3 text-sm transition-colors hover:bg-bg ${
          colorOnly ? 'border-accent bg-accent/10 font-medium' : 'border-border'
        }`}
      >
        <span
          aria-hidden="true"
          className="h-3.5 w-3.5 rounded-full"
          style={{
            background: 'conic-gradient(#ef4444, #eab308, #22c55e, #3b82f6, #a855f7, #ef4444)',
            opacity: colorOnly ? 1 : 0.55,
          }}
        />
        {t('filter.color')}
      </button>

      <div className="ml-auto flex items-center gap-1.5">
        {filters.active.length > 0 && (
          <button
            type="button"
            className="btn-icon"
            onClick={() => {
              setSearch('');
              filters.clear();
            }}
            title={t('filter.reset')}
            aria-label={t('filter.reset')}
          >
            <Icon name="reset" />
          </button>
        )}
        <a className="btn-primary text-sm" href={exportHref} download title={exportTitle}>
          <Icon name="download" />
          {t('filter.exportShort')}
        </a>
      </div>
    </section>
  );
}

/**
 * « Mnemo ne contient rien avant le 01.09.2025 » — quand la période choisie commence avant
 * les premières données. Sans ce mot, un total sur juillet–juin passerait pour complet.
 */
export function DataStartNotice({
  t,
  day,
  from,
  coverage,
}: {
  t: T;
  day: (d: string) => string;
  from: string | null;
  coverage: CoverageDto | null;
}) {
  const oldest = coverage?.months[0]?.period;
  if (!from || !oldest) return null;
  const first = `${oldest}-01`;
  if (from >= first) return null;
  return (
    <p className="flex items-center gap-2 rounded-lg border border-warn/50 bg-surface px-3 py-2 text-sm">
      <span aria-hidden="true" className="h-2 w-2 shrink-0 rounded-full bg-warn" />
      {t('notice.dataStart', { date: day(first) })}
    </p>
  );
}
